import {
  Globe,
  Fingerprint,
  MessageCircle,
  Megaphone,
  Clapperboard,
  TrendingUp,
  MonitorSmartphone,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "digital-marketing",
    title: "Digital Marketing",
    icon: Globe,
    short: "Digital strategies built around your business objectives, channels and audience.",
    objectives:
      "Turn scattered marketing activity into one coherent digital system where every channel serves your business objectives and every action has a purpose.",
    problems: [
      "Marketing actions spread across channels with no unifying direction",
      "Budget spent on tactics that don't serve a clear strategy",
      "No visibility on what is actually driving results",
    ],
    deliverables: [
      "A complete audit of your current digital presence",
      "A channel strategy built around your audience and objectives",
      "A prioritised 90-day execution roadmap",
      "Ongoing performance tracking and clear reporting",
    ],
    method: [
      { title: "Audit", text: "We assess your presence, channels and data." },
      { title: "Plan", text: "We define objectives, audiences and priorities." },
      { title: "Deploy", text: "We roll out the strategy channel by channel." },
      { title: "Refine", text: "We track results and adjust continuously." },
    ],
  },
  {
    slug: "brand-strategy-identity",
    title: "Brand Strategy & Identity",
    icon: Fingerprint,
    short: "A strong, coherent and recognizable brand identity that sets you apart.",
    objectives:
      "Build a brand that people recognize, remember and trust — with a clear position, a distinct voice and a coherent identity across every touchpoint.",
    problems: [
      "A brand that blends in and struggles to justify its value",
      "Inconsistent visuals and messaging across channels",
      "No clear positioning against competitors",
    ],
    deliverables: [
      "Brand positioning and messaging framework",
      "Visual identity direction (logo usage, palette, typography)",
      "Tone of voice and brand guidelines",
      "A rollout plan for every customer touchpoint",
    ],
    method: [
      { title: "Discover", text: "We study your market, audience and competitors." },
      { title: "Position", text: "We define what makes you unmistakable." },
      { title: "Design", text: "We shape the identity around that position." },
      { title: "Systemize", text: "We document it so it stays coherent." },
    ],
  },
  {
    slug: "social-media-management",
    title: "Social Media Management",
    icon: MessageCircle,
    short: "Content creation, account management and community growth across platforms.",
    objectives:
      "Turn your social channels into a real business asset: a consistent presence, a growing community and content that serves your brand strategy.",
    problems: [
      "Irregular posting with no editorial direction",
      "Content that gets likes but drives no business",
      "No time or structure to manage channels properly",
    ],
    deliverables: [
      "A platform-by-platform content strategy",
      "A monthly editorial calendar and content production",
      "Community management and engagement routines",
      "Monthly performance reports with clear insights",
    ],
    method: [
      { title: "Listen", text: "We learn your audience and your market's codes." },
      { title: "Structure", text: "We build pillars, formats and a calendar." },
      { title: "Publish", text: "We create and ship content consistently." },
      { title: "Grow", text: "We double down on what builds community." },
    ],
  },
  {
    slug: "paid-advertising",
    title: "Paid Advertising",
    icon: Megaphone,
    short: "Campaigns on Meta Ads, Google Ads and every relevant platform for your market.",
    objectives:
      "Make every euro of ad spend accountable: campaigns engineered around your margins, your audience and the actions that actually grow your business.",
    problems: [
      "Ad budgets burned on clicks that never convert",
      "Campaigns launched without a funnel behind them",
      "No reliable way to measure return on ad spend",
    ],
    deliverables: [
      "A campaign structure matched to your funnel",
      "Audience research, targeting and creative direction",
      "Conversion tracking set up end to end",
      "Continuous optimization and transparent reporting",
    ],
    method: [
      { title: "Frame", text: "We define the offer, the audience and the goal." },
      { title: "Build", text: "We structure campaigns, audiences and creatives." },
      { title: "Launch", text: "We go live with controlled budgets." },
      { title: "Scale", text: "We scale winners and cut what underperforms." },
    ],
  },
  {
    slug: "content-creation",
    title: "Content Creation",
    icon: Clapperboard,
    short: "Visuals, videos and ads designed to stop the scroll and capture attention.",
    objectives:
      "Produce content with intent: visuals, videos and ad creatives that carry your brand, stop the scroll and move people toward action.",
    problems: [
      "Content that looks generic and gets ignored",
      "No consistent visual standard across platforms",
      "Good ideas that never become finished assets",
    ],
    deliverables: [
      "A creative direction aligned with your brand",
      "Photo, video and graphic assets built per channel",
      "Ad creatives designed for performance",
      "A reusable content system, not one-off pieces",
    ],
    method: [
      { title: "Brief", text: "We define the message and the formats." },
      { title: "Concept", text: "We develop creative directions to validate." },
      { title: "Produce", text: "We shoot, design and edit the assets." },
      { title: "Iterate", text: "We learn from performance and improve." },
    ],
  },
  {
    slug: "growth-strategy",
    title: "Growth Strategy",
    icon: TrendingUp,
    short: "Optimizing acquisition, conversion and customer retention end to end.",
    objectives:
      "Look at your whole funnel — acquisition, conversion, retention — and engineer the levers that turn marketing effort into sustainable growth.",
    problems: [
      "Traffic that arrives but never converts",
      "Customers acquired once and never retained",
      "Growth decisions made on instinct instead of data",
    ],
    deliverables: [
      "A full-funnel audit with prioritized growth levers",
      "Conversion optimization of key pages and flows",
      "Retention and lifetime-value action plan",
      "A measurement framework your team can steer by",
    ],
    method: [
      { title: "Map", text: "We chart your funnel from first click to repeat purchase." },
      { title: "Diagnose", text: "We find the leaks that cost you growth." },
      { title: "Fix", text: "We optimize the highest-impact points first." },
      { title: "Compound", text: "We build on what measurably works." },
    ],
  },
  {
    slug: "website-landing-pages",
    title: "Website & Landing Pages",
    icon: MonitorSmartphone,
    short: "Professional websites and conversion pages engineered to generate leads.",
    objectives:
      "Build a website that works as hard as you do: fast, credible and engineered to turn visitors into enquiries, bookings and customers.",
    problems: [
      "A website that looks fine but generates nothing",
      "Slow, confusing pages that lose visitors in seconds",
      "No clear path from visit to contact",
    ],
    deliverables: [
      "A conversion-focused site or landing page architecture",
      "Design and copy aligned with your brand strategy",
      "Technical SEO and performance fundamentals",
      "Analytics and lead tracking wired in from day one",
    ],
    method: [
      { title: "Architect", text: "We structure pages around your visitor's decision." },
      { title: "Write", text: "We craft copy that makes the value obvious." },
      { title: "Build", text: "We develop fast, responsive, search-ready pages." },
      { title: "Convert", text: "We test and refine the paths to contact." },
    ],
  },
];
